import { useEffect, useState } from "react";
import { FolderOpen, Save, FileWarning, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { updateCase } from "@/lib/api";
import { MAHKEME_TURLERI } from "@/lib/constants";

const DEFAULTS = {
  yd_li: false,
  durusmali: false,
  tek_hakim: false,
  heyet: false,
  dilekce_redden_yeni: false,
  eski_esas_no: "",
};

const FLAGS = [
  { key: "yd_li", label: "YD'li", help: "Yürütmeyi Durdurma talepli dosya" },
  { key: "durusmali", label: "Duruşmalı", help: "Duruşma yapılacak" },
  { key: "tek_hakim", label: "Tek Hakim", help: "Tek hakimli heyet" },
  { key: "heyet", label: "Heyet", help: "Toplu heyet" },
  { key: "dilekce_redden_yeni", label: "Dilekçe Redden Doğan Yeni", help: "Önceki dilekçenin reddedilmesi sonrası açılan yeni dosya" },
];

export default function DosyaBilgileriForm({ caseData, onSaved }) {
  const initial = { ...DEFAULTS, ...(caseData.dosya_bilgileri || {}) };
  const [form, setForm] = useState(initial);
  const [mahkemeTuru, setMahkemeTuru] = useState(caseData.mahkeme_turu || "hukuk");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const next = { ...DEFAULTS, ...(caseData.dosya_bilgileri || {}) };
    setForm(next);
    setMahkemeTuru(caseData.mahkeme_turu || "hukuk");
    setDirty(false);
  }, [caseData.id]); // eslint-disable-line

  const change = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const changeMt = (v) => { setMahkemeTuru(v); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await updateCase(caseData.id, { dosya_bilgileri: form, mahkeme_turu: mahkemeTuru });
      toast.success("Dosya bilgileri kaydedildi");
      setDirty(false);
      onSaved?.();
    } catch {
      toast.error("Kaydedilemedi");
    } finally { setSaving(false); }
  };

  return (
    <Card className="bg-slate-900/70 border-slate-800" data-testid="dosya-bilgileri-card">
      <CardContent className="p-5 space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-amber-500" />
            <div>
              <div className="font-serif text-lg text-slate-100">Dosya Bilgileri</div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Dava açılış özellikleri</div>
            </div>
          </div>
          <Button size="sm" onClick={save} disabled={!dirty || saving}
            className="bg-amber-600 hover:bg-amber-700 text-slate-950 disabled:opacity-40"
            data-testid="dosya-bilgileri-save-btn">
            <Save className="w-3.5 h-3.5 mr-1" />
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-400 flex items-center gap-1.5">
              <Scale className="w-3 h-3 text-amber-500" /> Mahkeme Türü
            </Label>
            <Select value={mahkemeTuru} onValueChange={changeMt}>
              <SelectTrigger className="bg-slate-800 border-slate-700 mt-1 text-slate-100" data-testid="dosya-mahkeme-turu">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {MAHKEME_TURLERI.map((m) => (
                  <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 mt-1">Raporlarda filtreleme için kullanılır</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {FLAGS.map((f) => (
            <label key={f.key}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition
                ${form[f.key] ? "border-amber-700/60 bg-amber-950/20" : "border-slate-800 bg-slate-950/40 hover:border-slate-700"}`}
              data-testid={`dosya-flag-${f.key}`}>
              <Checkbox
                checked={Boolean(form[f.key])}
                onCheckedChange={(v) => change(f.key, Boolean(v))}
                className="mt-0.5 border-slate-600 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
              />
              <div className="min-w-0">
                <div className="text-sm text-slate-100 font-medium">{f.label}</div>
                <div className="text-[11px] text-slate-500">{f.help}</div>
              </div>
            </label>
          ))}
        </div>

        <div className="pt-2 border-t border-slate-800">
          <Label className="text-xs text-slate-400 flex items-center gap-1.5">
            <FileWarning className="w-3 h-3 text-amber-500" />
            Dilekçe Red İse — Eski Esas No
          </Label>
          <Input
            value={form.eski_esas_no || ""}
            onChange={(e) => change("eski_esas_no", e.target.value)}
            placeholder="örn: 2023/412 E."
            className="bg-slate-800 border-slate-700 mt-1 text-slate-100 font-mono max-w-md"
            data-testid="dosya-eski-esas-input"
          />
          <div className="text-[11px] text-slate-500 mt-1">
            Reddedilen önceki dilekçenin esas numarası (varsa)
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
